import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, BookOpen, ExternalLink, Download } from 'lucide-react';

/** Static path — the PDF lives in client/public and is served at the site root. */
const PLAYBOOK_URL = '/onboarding-playbook.pdf';

export interface PlaybookModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Onboarding Playbook viewer.
 *
 * Shows the "Tägliche Systempflege" onboarding playbook as a reference for
 * all employees. On desktop/tablet the PDF is embedded in an iframe; on
 * mobile (where inline PDF iframes are unreliable on iOS/Android) we show a
 * card that opens the PDF in the device's native viewer instead.
 */
export function PlaybookModal({ isOpen, onClose }: PlaybookModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Escape to close + lock body scroll while open
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="playbook-modal-title"
    >
      <div className="flex w-full max-w-5xl h-[92vh] flex-col rounded-xl border border-slate-700 bg-slate-800 shadow-xl animate-in fade-in-0 zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-700 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-500/15 text-orange-400">
              <BookOpen className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h2
                id="playbook-modal-title"
                className="truncate text-base font-semibold text-slate-100 sm:text-lg"
              >
                Onboarding Playbook
              </h2>
              <p className="truncate text-xs text-slate-400">Tägliche Systempflege</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-200"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="relative flex-1 overflow-hidden bg-slate-900">
          {/* Desktop / tablet: embedded PDF */}
          <iframe
            src={`${PLAYBOOK_URL}#view=FitH&navpanes=0`}
            title="Onboarding Playbook"
            className="hidden h-full w-full md:block"
          />

          {/* Mobile: open in native viewer */}
          <div className="flex h-full flex-col items-center justify-center gap-5 px-6 text-center md:hidden">
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500/15 text-orange-400">
              <BookOpen className="h-8 w-8" />
            </span>
            <div className="space-y-1">
              <p className="text-base font-semibold text-slate-100">
                Onboarding Playbook
              </p>
              <p className="text-sm text-slate-400">
                Öffne die Anleitung zur täglichen Systempflege im PDF-Viewer.
              </p>
            </div>
            <a
              href={PLAYBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-orange-600"
            >
              <ExternalLink className="h-4 w-4" />
              Playbook öffnen
            </a>
          </div>
        </div>

        {/* Footer — universal fallback links */}
        <div className="flex items-center justify-end gap-4 border-t border-slate-700 px-4 py-3 sm:px-6">
          <a
            href={PLAYBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 transition-colors hover:text-slate-200"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            In neuem Tab öffnen
          </a>
          <a
            href={PLAYBOOK_URL}
            download
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 transition-colors hover:text-slate-200"
          >
            <Download className="h-3.5 w-3.5" />
            Herunterladen
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}
